"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "next/navigation";

import { trackCheckoutStarted, trackPricingCtaClicked } from "@/lib/analytics";
import { submitPayFastCheckout } from "@/lib/payfast/submitCheckout";
import { EVENTS } from "@/lib/analytics/trackEvent";
import { supabase } from "@/lib/supabaseClient";
import { PLAN_DISPLAY, PLAN_LIMITS, PLANS } from "@/lib/plans/planConfig";
import { safeTrack } from "@/lib/safeTrack";

export default function PricingPage() {
  type PlanLimitKey = keyof (typeof PLAN_LIMITS)[typeof PLANS.FREE];
  const searchParams = useSearchParams();
  const highlight = (searchParams?.get("highlight") || "").toLowerCase();
  const from = searchParams?.get("from") || "";
  const manage = searchParams?.get("manage") === "1";

  const [loadingPlan, setLoadingPlan] = useState<"pro" | "team" | null>(null);
  const [managingBilling, setManagingBilling] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState<{ plan?: string; status?: string; currentPeriodEnd?: string | null } | null>(null);

  async function startCheckout(plan: "pro" | "team") {
    trackPricingCtaClicked(plan, "pricing_table", from);
    setLoadingPlan(plan);
    setError("");
    try {
      if (!supabase) throw new Error("Sign-in isn't available right now. Please try again in a moment.");
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error("Please sign in first.");

      const response = await fetch("/api/billing/create-checkout-session", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          plan: plan === "team" ? "premium" : "pro",
          returnTo: "pricing",
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || "Checkout didn't start. Please try again.");
      trackCheckoutStarted(plan, "pricing_table", from);
      submitPayFastCheckout(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Checkout failed";
      setError(msg);
      setLoadingPlan(null);
    }
  }

  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);

  async function openBillingPortal() {
    setManagingBilling(true);
    setError("");
    try {
      if (!supabase) throw new Error("Sign-in isn't available right now. Please try again in a moment.");
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error("Please sign in first.");

      const response = await fetch("/api/billing/create-portal-session", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || "We couldn't open billing right now. Please try again.");
      if (data?.url) window.location.href = data.url as string;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "We couldn't open billing right now. Please try again.";
      setError(msg);
    } finally {
      setManagingBilling(false);
    }
  }

  async function cancelSubscription() {
    setCancellingSubscription(true);
    setError("");
    try {
      if (!supabase) throw new Error("Sign-in isn't available right now. Please try again in a moment.");
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error("Please sign in first.");

      const response = await fetch("/api/billing/cancel-subscription", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.error || "Cancellation failed");
      setCancelConfirm(false);
      await loadStatus();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Cancellation failed";
      setError(msg);
    } finally {
      setCancellingSubscription(false);
    }
  }

  async function loadStatus() {
    try {
      if (!supabase) return;
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) return;

      const response = await fetch("/api/billing/status", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (response.ok) setStatus(data || null);
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    loadStatus();
    safeTrack(
      async () => {
        await fetch("/api/analytics/track", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            eventName: EVENTS.PRICING_PAGE_VIEWED,
            properties: { from, highlight },
          }),
          keepalive: true,
        });
      },
      "pricing_page_viewed",
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentRuntimePlan = String(status?.plan || "free").toLowerCase() === "premium" ? PLANS.TEAM : String(status?.plan || "free").toLowerCase() === "pro" ? PLANS.PRO : PLANS.FREE;

  const planOrder = [PLANS.FREE, PLANS.PRO, PLANS.TEAM];
  const tableRows = useMemo<Array<{ key: PlanLimitKey; label: string }>>(
    () => [
      { key: "maxMedications", label: "Medications tracked" },
      { key: "maxUsersPerOrg", label: "Users per organization" },
      { key: "pillCountsPerMonth", label: "Pill counts per month" },
      { key: "expiryAlertsEnabled", label: "Expiry alerts" },
      { key: "bulkImportEnabled", label: "Bulk import" },
      { key: "exportReportsEnabled", label: "Export reports" },
      { key: "auditLogEnabled", label: "Audit log" },
      { key: "aiPillCountEnabled", label: "AI pill count assist" },
      { key: "prioritySupportEnabled", label: "Support level" },
      { key: "dataRetentionDays", label: "Data retention" },
    ],
    [],
  );

  function renderLimitValue(value: unknown, key: PlanLimitKey) {
    if (typeof value === "boolean") return value ? "✓" : "—";
    if (typeof value === "number") {
      if (value === -1) return "Unlimited";
      if (key === "dataRetentionDays") return `${value} days`;
      return String(value);
    }
    return String(value ?? "—");
  }

  return (
    <div id="main" tabIndex={-1} style={{ minHeight: "100vh", color: "var(--ed-text)", padding: "1.25rem 1rem 2.6rem", fontFamily: "var(--font-sans)", background: "linear-gradient(180deg, var(--ed-bg-start) 0%, var(--ed-bg-mid) 100%)", outline: "none" }}>
      <div style={{ maxWidth: "1160px", margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.75rem", marginBottom: "1rem", flexWrap: "wrap" }}>
          <Link href="/dashboard" className="ed-btn-secondary" style={{ textDecoration: "none", padding: "0.5rem 0.85rem", fontSize: "0.8rem" }}>
            Back to dashboard
          </Link>
          <button type="button" onClick={openBillingPortal} className="ed-btn-ghost" disabled={managingBilling} style={{ padding: "0.5rem 0.85rem", fontSize: "0.8rem" }}>
            {managingBilling ? "Opening…" : "Manage billing"}
          </button>
        </div>

        {manage && status && currentRuntimePlan !== PLANS.FREE && (
          <section className="ed-card-glass" style={{ marginBottom: "1rem", borderRadius: 14, padding: "0.9rem 1rem", border: "1px solid var(--ed-ui-critical-border)", background: "var(--ed-ui-critical-bg)" }}>
            <h2 style={{ margin: "0 0 0.5rem", fontSize: "0.95rem", fontWeight: 800, color: "var(--ed-ui-critical)" }}>Manage Subscription</h2>
            <p style={{ margin: "0 0 0.75rem", fontSize: "0.84rem", color: "var(--ed-text-secondary)" }}>
              Current plan: <strong style={{ color: "var(--ed-text)" }}>{currentRuntimePlan.toUpperCase()}</strong> · {String(status.status || "active")}
            </p>
            {cancelConfirm ? (
              <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ fontSize: "0.84rem", color: "var(--ed-text-secondary)" }}>Are you sure? This will cancel your subscription immediately.</span>
                <button type="button" className="ed-btn-primary" style={{ background: "var(--ed-ui-critical)", borderColor: "var(--ed-ui-critical)" }} disabled={cancellingSubscription} onClick={() => void cancelSubscription()}>
                  {cancellingSubscription ? "Cancelling…" : "Yes, cancel"}
                </button>
                <button type="button" className="ed-btn-ghost" onClick={() => setCancelConfirm(false)}>Keep subscription</button>
              </div>
            ) : (
              <button type="button" className="ed-btn-secondary" onClick={() => setCancelConfirm(true)}>Cancel subscription</button>
            )}
          </section>
        )}

        <section style={{ marginBottom: "1.35rem" }}>
          <h1 style={{ margin: 0, fontWeight: 800, fontSize: "clamp(1.85rem, 4vw, 2.45rem)", lineHeight: 1.08, color: "var(--ed-text)", letterSpacing: "-0.03em" }}>
            Stop Losing Money to Expired Stock
          </h1>
          <p style={{ margin: "0.65rem 0 0", color: "var(--ed-text-secondary)", fontSize: "0.98rem", lineHeight: 1.65, maxWidth: "46rem" }}>
            ExpiryDesk helps pharmacies catch expiry risk before it becomes waste, fines, or liability.
          </p>
          {from ? (
            <div className="ed-card-glass" style={{ marginTop: "0.8rem", borderRadius: 12, border: "1px solid var(--ed-ui-info-border)", background: "var(--ed-ui-info-bg)", padding: "0.6rem 0.75rem", fontSize: "0.84rem", color: "var(--ed-ui-info)", fontWeight: 600 }}>
              You tried to use <strong>{from}</strong> — it&apos;s available on Pro.
            </div>
          ) : null}
          <div className="ed-card-glass" style={{ marginTop: "0.85rem", borderRadius: 12, padding: "0.75rem 0.95rem", border: "1px solid var(--ed-border)", background: "var(--ed-glass-bg)" }}>
            <p style={{ margin: 0, fontSize: "0.88rem", lineHeight: 1.6, color: "var(--ed-text-secondary)", fontWeight: 600 }}>
              <strong style={{ color: "var(--ed-text)" }}>Modern SaaS pricing:</strong> stay on Free to load lines, see urgency, and use the in-app
              guides. Move to Pro when you want{" "}
              <strong style={{ color: "var(--ed-text)" }}>automation</strong> (digests, exports, audit) and higher limits — pay once the value is
              obvious on your own stock.
            </p>
          </div>
          <div
            className="ed-card-glass"
            style={{
              marginTop: "0.75rem",
              borderRadius: 12,
              padding: "0.75rem 0.95rem",
              border: "1px solid var(--ed-ui-info-border)",
              background: "linear-gradient(135deg, var(--ed-ui-info-bg) 0%, var(--ed-glass-bg) 100%)",
            }}
          >
            <p style={{ margin: 0, fontSize: "0.82rem", lineHeight: 1.6, color: "var(--ed-ui-info)", fontWeight: 700 }}>
              <strong style={{ color: "var(--ed-text)" }}>ExpiryDesk Intelligence Network™</strong> — a preview is on your dashboard today (health
              score, supplier signals when you tag suppliers, tasks, lessons).{" "}
              <strong style={{ color: "var(--ed-text)" }}>Team</strong> is the lane for opt-in, privacy-first networked intelligence as it ships — no
              fake peer percentages, only what we can stand behind.
            </p>
          </div>
        </section>

        <section className="ed-card-glass" style={{ marginBottom: "1rem", borderRadius: 14, padding: "0.85rem 1rem" }}>
          {/* TODO: replace placeholder quotes with live customer testimonials */}
          <div style={{ display: "grid", gap: "0.45rem", fontSize: "0.84rem", color: "var(--ed-text-secondary)" }}>
            <div>&quot;We cut expiry firefighting time in week one.&quot; — Pharmacy lead, Gauteng</div>
            <div>&quot;Batch capture alone paid for itself in one month.&quot; — Inventory manager, Durban</div>
            <div>&quot;Audits are calmer because the trail is finally clear.&quot; — Operations manager, Cape Town</div>
          </div>
        </section>

        <section className="ed-card-glass" style={{ borderRadius: 16, overflowX: "auto", marginBottom: "1.1rem", border: "1px solid rgba(15,23,42,0.08)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "900px" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "0.8rem 0.75rem", fontSize: "0.72rem", color: "var(--ed-text-muted)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Feature</th>
                {planOrder.map((plan) => {
                  const display = PLAN_DISPLAY[plan];
                  const emphasized = highlight === plan || (plan === PLANS.PRO && highlight === "");
                  return (
                    <th
                      key={plan}
                      style={{
                        textAlign: "left",
                        padding: "0.8rem 0.75rem",
                        borderLeft: "1px solid var(--ed-border)",
                        background: emphasized ? "var(--ed-ui-info-bg)" : "transparent",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
                        <span style={{ fontWeight: 800, fontSize: "0.95rem", color: "var(--ed-text)" }}>{display.name}</span>
                        {plan === PLANS.PRO ? (
                          <span style={{ fontSize: "0.62rem", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--ed-ui-info)", background: "var(--ed-ui-info-bg)", borderRadius: 999, padding: "0.18rem 0.45rem" }}>
                            Recommended
                          </span>
                        ) : null}
                      </div>
                      <div style={{ marginTop: "0.2rem", fontSize: "0.84rem", fontWeight: 700, color: "var(--ed-text)" }}>
                        {display.price === 0 ? "Free" : `R${display.price}/${display.billingPeriod}`}
                      </div>
                      <div style={{ fontSize: "0.72rem", color: "var(--ed-text-secondary)" }}>{display.tagline}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row) => (
                <tr key={row.key} style={{ borderTop: "1px solid rgba(148,163,184,0.2)" }}>
                  <td style={{ padding: "0.58rem 0.75rem", fontSize: "0.8rem", color: "var(--ed-text-secondary)", fontWeight: 600 }}>{row.label}</td>
                  {planOrder.map((plan) => {
                    const emphasized = highlight === plan || (plan === PLANS.PRO && highlight === "");
                    return (
                      <td key={`${row.key}-${plan}`} style={{ padding: "0.58rem 0.75rem", borderLeft: "1px solid var(--ed-border)", fontSize: "0.8rem", color: "var(--ed-text)", background: emphasized ? "var(--ed-ui-info-bg)" : "transparent" }}>
                        {renderLimitValue(PLAN_LIMITS[plan][row.key], row.key)}
                      </td>
                    );
                  })}
                </tr>
              ))}
              <tr style={{ borderTop: "1px solid rgba(148,163,184,0.2)" }}>
                <td style={{ padding: "0.75rem" }} />
                <td style={{ padding: "0.75rem", borderLeft: "1px solid rgba(148,163,184,0.15)" }}>
                  <button type="button" className="ed-btn-secondary" disabled style={{ opacity: 0.6, width: "100%" }}>
                    {currentRuntimePlan === PLANS.FREE ? "Current Plan" : "Free plan"}
                  </button>
                </td>
                <td style={{ padding: "0.75rem", borderLeft: "1px solid rgba(148,163,184,0.15)", background: highlight === PLANS.PRO || highlight === "" ? "rgba(240,249,255,0.6)" : "transparent" }}>
                  <button type="button" className="ed-btn-primary" style={{ width: "100%" }} disabled={loadingPlan !== null} onClick={() => void startCheckout("pro")}>
                    {loadingPlan === "pro" ? "Starting…" : "Upgrade to Pro — R749/month"}
                  </button>
                </td>
                <td style={{ padding: "0.75rem", borderLeft: "1px solid rgba(148,163,184,0.15)", background: highlight === PLANS.TEAM ? "rgba(240,249,255,0.6)" : "transparent" }}>
                  <button type="button" className="ed-btn-secondary" style={{ width: "100%" }} disabled={loadingPlan !== null} onClick={() => void startCheckout("team")}>
                    {loadingPlan === "team" ? "Starting…" : "Upgrade to Team — R1499/month"}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </section>

        <section className="ed-card-glass" style={{ borderRadius: 14, padding: "0.9rem 1rem", marginBottom: "1rem" }}>
          <h2 style={{ margin: "0 0 0.5rem", fontSize: "1rem", fontWeight: 800, color: "var(--ed-text)" }}>FAQ</h2>
          <div style={{ display: "grid", gap: "0.6rem", fontSize: "0.84rem", color: "var(--ed-text-secondary)", lineHeight: 1.55 }}>
            <div><strong style={{ color: "var(--ed-text)" }}>Can I cancel at any time?</strong><br />Yes. Cancel anytime from billing portal, no lock-in.</div>
            <div><strong style={{ color: "var(--ed-text)" }}>What happens to my data if I downgrade?</strong><br />Data is retained for 90 days, then archived.</div>
            <div><strong style={{ color: "var(--ed-text)" }}>Is there a free trial for Pro?</strong><br />No. Pay upfront for Pro or Team — no trial period, cancel anytime from the billing portal.</div>
            <div><strong style={{ color: "var(--ed-text)" }}>Do you support multiple locations?</strong><br />Team supports multiple users per organization and multi-site operations.</div>
            <div><strong style={{ color: "var(--ed-text)" }}>Is my data secure?</strong><br />Yes. ExpiryDesk uses authenticated APIs, validated inputs, hardened route boundaries, and secure billing integrations.</div>
          </div>
        </section>

        <section className="ed-card-glass" style={{ borderRadius: 14, padding: "1rem 1.05rem", textAlign: "center", background: "linear-gradient(180deg, var(--ed-ui-info-bg) 0%, var(--ed-glass-bg) 100%)" }}>
          <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "var(--ed-text)" }}>Ready to protect your pharmacy from expiry waste?</h3>
          <button type="button" className="ed-btn-primary" style={{ marginTop: "0.7rem", padding: "0.6rem 1.1rem" }} onClick={() => void startCheckout("pro")} disabled={loadingPlan !== null}>
            {loadingPlan === "pro" ? "Starting…" : "Get Started with Pro"}
          </button>
        </section>

        {status && (
          <div style={{ marginTop: "0.8rem", fontSize: "0.78rem", color: "var(--ed-text-secondary)" }}>
            Current plan: <strong style={{ color: "var(--ed-text)" }}>{currentRuntimePlan.toUpperCase()}</strong> · {String(status.status || "active").replace(/_/g, " ")}
          </div>
        )}

        {error && (
          <div style={{ marginTop: "1.1rem", background: "var(--ed-ui-critical-bg)", border: "1px solid var(--ed-ui-critical-border)", borderRadius: "var(--ed-radius-sm)", padding: "0.9rem 1rem", color: "var(--ed-ui-critical)" }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
